import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { groupingsService } from '../../services/api/groupingsService';
import { GroupingsConfig } from '../../types/groupings';
import './FXGOSettings.css';

// ============================================================
// Types
// ============================================================

interface SpotCurrencyPairRow {
  currencyPair: string;
  grouping1: string;
  grouping2: string;
}

interface ForwardCurrencyPairRow {
  currencyPair: string;
  grouping1: string;
  grouping2: string;
}

interface ValueMappingEntry {
  from: string;
  to: string;
}

type TransformationType = 'direct' | 'valueMapping';

interface CustomFieldMapping {
  sourceField: string;
  targetField: string;
  transformation: TransformationType;
  valueMapping: ValueMappingEntry[];
}

export interface FXGODefaultsSettings {
  spotRows: SpotCurrencyPairRow[];
  forwardRows: ForwardCurrencyPairRow[];
  customFieldMappings: CustomFieldMapping[];
}

interface FXGOSettingsProps {
  settings: FXGODefaultsSettings | null;
  onSave: (settings: FXGODefaultsSettings) => Promise<void>;
  saving: boolean;
}

// ============================================================
// Constants
// ============================================================

const OTHER_KEY = '__OTHER__';
const AVAILABLE_CURRENCY_PAIRS = ['USD/CLP', 'EUR/CLP', 'USD/EUR', 'GBP/CLP', 'JPY/CLP'];

const makeSpotRow = (pair: string): SpotCurrencyPairRow => ({
  currencyPair: pair,
  grouping1: '',
  grouping2: '',
});

const makeForwardRow = (pair: string): ForwardCurrencyPairRow => ({
  currencyPair: pair,
  grouping1: '',
  grouping2: '',
});

const DEFAULT_SETTINGS: FXGODefaultsSettings = {
  spotRows: [
    { ...makeSpotRow('USD/CLP') },
    { ...makeSpotRow(OTHER_KEY) },
  ],
  forwardRows: [
    { ...makeForwardRow('USD/CLP') },
    { ...makeForwardRow(OTHER_KEY) },
  ],
  customFieldMappings: [],
};

// ============================================================
// Component
// ============================================================

const FXGOSettings: React.FC<FXGOSettingsProps> = ({ settings, onSave, saving }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';
  const [groupingsConfig, setGroupingsConfig] = useState<GroupingsConfig | null>(null);

  useEffect(() => {
    if (!clientId) return;
    groupingsService.getGroupings(clientId).then(config => {
      if (config) setGroupingsConfig(config);
    });
  }, [clientId]);

  const [formData, setFormData] = useState<FXGODefaultsSettings>(() => {
    const initial = settings ?? DEFAULT_SETTINGS;
    const withOther = { ...initial };
    if (!withOther.spotRows.some((r) => r.currencyPair === OTHER_KEY)) {
      withOther.spotRows = [...withOther.spotRows, makeSpotRow(OTHER_KEY)];
    }
    if (!withOther.forwardRows.some((r) => r.currencyPair === OTHER_KEY)) {
      withOther.forwardRows = [...withOther.forwardRows, makeForwardRow(OTHER_KEY)];
    }
    if (!withOther.customFieldMappings) {
      withOther.customFieldMappings = [];
    }
    return withOther;
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [newSpotPair, setNewSpotPair] = useState('');
  const [newForwardPair, setNewForwardPair] = useState('');

  // Ensure __OTHER__ catch-all row always exists in both tables
  const ensureOtherRows = (data: FXGODefaultsSettings): FXGODefaultsSettings => {
    const result = { ...data };
    if (!result.spotRows.some((r) => r.currencyPair === OTHER_KEY)) {
      result.spotRows = [...result.spotRows, makeSpotRow(OTHER_KEY)];
    }
    if (!result.forwardRows.some((r) => r.currencyPair === OTHER_KEY)) {
      result.forwardRows = [...result.forwardRows, makeForwardRow(OTHER_KEY)];
    }
    if (!result.customFieldMappings) {
      result.customFieldMappings = [];
    }
    return result;
  };

  // Sync form data when settings prop changes (e.g. after initial load)
  useEffect(() => {
    if (settings) {
      setFormData(ensureOtherRows(settings));
      setHasChanges(false);
    }
  }, [settings]);

  // ----------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------

  const updateField = (updater: (draft: FXGODefaultsSettings) => void) => {
    const next = JSON.parse(JSON.stringify(formData)) as FXGODefaultsSettings;
    updater(next);
    setFormData(next);
    setHasChanges(true);
  };

  const handleSave = async () => {
    await onSave(formData);
    setHasChanges(false);
  };

  const handleReset = () => {
    setFormData(settings ?? DEFAULT_SETTINGS);
    setHasChanges(false);
  };

  // ----------------------------------------------------------
  // Spot row helpers
  // ----------------------------------------------------------

  const addSpotRow = () => {
    if (!newSpotPair) return;
    if (formData.spotRows.some((r) => r.currencyPair === newSpotPair)) return;
    updateField((d) => {
      const otherIdx = d.spotRows.findIndex((r) => r.currencyPair === OTHER_KEY);
      const newRow = makeSpotRow(newSpotPair);
      if (otherIdx >= 0) {
        d.spotRows.splice(otherIdx, 0, newRow);
      } else {
        d.spotRows.push(newRow);
      }
    });
    setNewSpotPair('');
  };

  const removeSpotRow = (index: number) => {
    if (formData.spotRows[index].currencyPair === OTHER_KEY) return;
    updateField((d) => { d.spotRows.splice(index, 1); });
  };

  const updateSpotCell = (index: number, field: keyof SpotCurrencyPairRow, value: string) => {
    updateField((d) => { (d.spotRows[index] as any)[field] = value; });
  };

  // ----------------------------------------------------------
  // Forward row helpers
  // ----------------------------------------------------------

  const addForwardRow = () => {
    if (!newForwardPair) return;
    if (formData.forwardRows.some((r) => r.currencyPair === newForwardPair)) return;
    updateField((d) => {
      const otherIdx = d.forwardRows.findIndex((r) => r.currencyPair === OTHER_KEY);
      const newRow = makeForwardRow(newForwardPair);
      if (otherIdx >= 0) {
        d.forwardRows.splice(otherIdx, 0, newRow);
      } else {
        d.forwardRows.push(newRow);
      }
    });
    setNewForwardPair('');
  };

  const removeForwardRow = (index: number) => {
    if (formData.forwardRows[index].currencyPair === OTHER_KEY) return;
    updateField((d) => { d.forwardRows.splice(index, 1); });
  };

  const updateForwardCell = (index: number, field: keyof ForwardCurrencyPairRow, value: string) => {
    updateField((d) => { (d.forwardRows[index] as any)[field] = value; });
  };

  // ----------------------------------------------------------
  // Custom field mapping helpers
  // ----------------------------------------------------------

  const addCustomMapping = () => {
    updateField((d) => {
      d.customFieldMappings.push({
        sourceField: '',
        targetField: '',
        transformation: 'direct',
        valueMapping: [],
      });
    });
  };

  const removeCustomMapping = (index: number) => {
    updateField((d) => { d.customFieldMappings.splice(index, 1); });
  };

  const updateCustomMapping = (index: number, field: keyof CustomFieldMapping, value: any) => {
    updateField((d) => { (d.customFieldMappings[index] as any)[field] = value; });
  };

  const addValueMappingEntry = (mappingIndex: number) => {
    updateField((d) => {
      d.customFieldMappings[mappingIndex].valueMapping.push({ from: '', to: '' });
    });
  };

  const removeValueMappingEntry = (mappingIndex: number, entryIndex: number) => {
    updateField((d) => {
      d.customFieldMappings[mappingIndex].valueMapping.splice(entryIndex, 1);
    });
  };

  const updateValueMappingFrom = (mappingIndex: number, entryIndex: number, value: string) => {
    updateField((d) => {
      d.customFieldMappings[mappingIndex].valueMapping[entryIndex].from = value;
    });
  };

  const updateValueMappingTo = (mappingIndex: number, entryIndex: number, value: string) => {
    updateField((d) => {
      d.customFieldMappings[mappingIndex].valueMapping[entryIndex].to = value;
    });
  };

  // Get valid "to" values based on target field (dropdown for groupings, null = free text)
  const getValidTargetValues = (targetField: string): string[] | null => {
    if (targetField === 'Grouping1') {
      return groupingsConfig?.grouping1Values.filter(v => v.isActive).map(v => v.name) || [];
    }
    if (targetField === 'Grouping2') {
      return groupingsConfig?.grouping2Values.filter(v => v.isActive).map(v => v.name) || [];
    }
    return null; // free text for HedgeObject, Notes
  };

  // ----------------------------------------------------------
  // Derived
  // ----------------------------------------------------------

  const availableSpotPairs = AVAILABLE_CURRENCY_PAIRS.filter(
    (cp) => !formData.spotRows.some((r) => r.currencyPair === cp)
  );
  const availableForwardPairs = AVAILABLE_CURRENCY_PAIRS.filter(
    (cp) => !formData.forwardRows.some((r) => r.currencyPair === cp)
  );

  const pairLabel = (pair: string) => pair === OTHER_KEY ? t('admin.fxgo.table.otherPairs') : pair;

  // Build target field options — groupings use client-specific labels
  const targetFieldOptions: { value: string; label: string }[] = [
    ...(groupingsConfig?.grouping1Label ? [{ value: 'Grouping1', label: groupingsConfig.grouping1Label }] : []),
    ...(groupingsConfig?.grouping2Label ? [{ value: 'Grouping2', label: groupingsConfig.grouping2Label }] : []),
    { value: 'HedgeObject', label: t('admin.fxgo.customFieldMappings.targets.hedgeObject') },
    { value: 'Notes', label: t('admin.fxgo.customFieldMappings.targets.notes') },
  ];

  const transformationOptions: { value: TransformationType; label: string }[] = [
    { value: 'direct', label: t('admin.fxgo.customFieldMappings.transformations.direct') },
    { value: 'valueMapping', label: t('admin.fxgo.customFieldMappings.transformations.valueMapping') },
  ];

  // ----------------------------------------------------------
  // Render helpers
  // ----------------------------------------------------------

  const renderSelect = (value: string, options: string[], onChange: (v: string) => void) => (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------

  return (
    <div className="fxgo-settings">
      <h2>{t('admin.fxgo.title')}</h2>

      <div className="fxgo-grid">
        <div className="fxgo-column">

          {/* Custom Field Mappings — at top */}
          <div className="setting-card">
            <div className="setting-header">
              <h3>{t('admin.fxgo.customFieldMappings.title')}</h3>
            </div>
            <p>{t('admin.fxgo.customFieldMappings.description')}</p>

            {formData.customFieldMappings.length > 0 && (
              <div className="fxgo-table-wrapper">
                <table className="fxgo-defaults-table">
                  <thead>
                    <tr>
                      <th>{t('admin.fxgo.customFieldMappings.sourceField')}</th>
                      <th>{t('admin.fxgo.customFieldMappings.targetField')}</th>
                      <th>{t('admin.fxgo.customFieldMappings.transformation')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.customFieldMappings.map((mapping, mIdx) => (
                      <React.Fragment key={mIdx}>
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={mapping.sourceField}
                              onChange={(e) => updateCustomMapping(mIdx, 'sourceField', e.target.value)}
                            />
                          </td>
                          <td>
                            <select value={mapping.targetField} onChange={(e) => updateCustomMapping(mIdx, 'targetField', e.target.value)}>
                              <option value="">--</option>
                              {targetFieldOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select value={mapping.transformation || 'direct'} onChange={(e) => updateCustomMapping(mIdx, 'transformation', e.target.value)}>
                              {transformationOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <button className="fxgo-remove-btn" onClick={() => removeCustomMapping(mIdx)} title={t('admin.fxgo.customFieldMappings.removeMapping')}>×</button>
                          </td>
                        </tr>
                        {mapping.transformation === 'valueMapping' && (
                          <tr className="fxgo-value-mapping-detail-row">
                            <td colSpan={4}>
                              <div className="fxgo-value-mapping-inline">
                                {mapping.valueMapping.map((entry, eIdx) => {
                                  const validValues = getValidTargetValues(mapping.targetField);
                                  return (
                                    <div key={eIdx} className="fxgo-value-mapping-row">
                                      <input
                                        type="text"
                                        value={entry.from}
                                        onChange={(e) => updateValueMappingFrom(mIdx, eIdx, e.target.value)}
                                        placeholder={t('admin.fxgo.customFieldMappings.valueMappingKey')}
                                      />
                                      <span className="fxgo-mapping-arrow">&rarr;</span>
                                      {validValues ? (
                                        <select
                                          value={entry.to}
                                          onChange={(e) => updateValueMappingTo(mIdx, eIdx, e.target.value)}
                                          style={{ flex: 1, maxWidth: 180 }}
                                        >
                                          <option value="">{t('admin.fxgo.customFieldMappings.valueMappingValue')}</option>
                                          {validValues.map((v) => (
                                            <option key={v} value={v}>{v}</option>
                                          ))}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          value={entry.to}
                                          onChange={(e) => updateValueMappingTo(mIdx, eIdx, e.target.value)}
                                          placeholder={t('admin.fxgo.customFieldMappings.valueMappingValue')}
                                        />
                                      )}
                                      <button className="fxgo-remove-btn" onClick={() => removeValueMappingEntry(mIdx, eIdx)}>×</button>
                                    </div>
                                  );
                                })}
                                <button className="fxgo-add-value-btn" onClick={() => addValueMappingEntry(mIdx)}>
                                  + {t('admin.fxgo.customFieldMappings.addValueMapping')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="fxgo-add-row">
              <button onClick={addCustomMapping}>
                + {t('admin.fxgo.customFieldMappings.addMapping')}
              </button>
            </div>
          </div>

          {/* Spot Defaults Table */}
          <div className="setting-card">
            <div className="setting-header">
              <h3>{t('admin.fxgo.spot.title')}</h3>
            </div>
            <p>{t('admin.fxgo.spot.description')}</p>

            <div className="fxgo-table-wrapper">
              <table className="fxgo-defaults-table">
                <thead>
                  <tr>
                    <th>{t('admin.fxgo.table.currencyPair')}</th>
                    <th>{groupingsConfig?.grouping1Label || t('admin.fxgo.table.grouping1')}</th>
                    {groupingsConfig?.grouping2Label && <th>{groupingsConfig.grouping2Label}</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.spotRows.map((row, idx) => (
                    <tr key={row.currencyPair} className={row.currencyPair === OTHER_KEY ? 'fxgo-other-row' : ''}>
                      <td className="fxgo-pair-cell">{pairLabel(row.currencyPair)}</td>
                      <td>{renderSelect(row.grouping1, ['', ...(groupingsConfig?.grouping1Values.filter(v => v.isActive).map(v => v.name) || [])], (v) => updateSpotCell(idx, 'grouping1', v))}</td>
                      {groupingsConfig?.grouping2Label && <td>{renderSelect(row.grouping2, ['', ...(groupingsConfig?.grouping2Values.filter(v => v.isActive).map(v => v.name) || [])], (v) => updateSpotCell(idx, 'grouping2', v))}</td>}
                      <td>
                        {row.currencyPair !== OTHER_KEY && (
                          <button className="fxgo-remove-btn" onClick={() => removeSpotRow(idx)} title={t('admin.fxgo.actions.remove')}>×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {availableSpotPairs.length > 0 && (
              <div className="fxgo-add-row">
                <select value={newSpotPair} onChange={(e) => setNewSpotPair(e.target.value)}>
                  <option value="">{t('admin.fxgo.forward.selectPair')}</option>
                  {availableSpotPairs.map((cp) => <option key={cp} value={cp}>{cp}</option>)}
                </select>
                <button onClick={addSpotRow} disabled={!newSpotPair}>{t('admin.fxgo.actions.add')}</button>
              </div>
            )}
          </div>

          {/* Forward Defaults Table */}
          <div className="setting-card">
            <div className="setting-header">
              <h3>{t('admin.fxgo.forward.title')}</h3>
            </div>
            <p>{t('admin.fxgo.forward.description')}</p>

            <div className="fxgo-table-wrapper">
              <table className="fxgo-defaults-table">
                <thead>
                  <tr>
                    <th>{t('admin.fxgo.table.currencyPair')}</th>
                    <th>{groupingsConfig?.grouping1Label || t('admin.fxgo.table.grouping1')}</th>
                    {groupingsConfig?.grouping2Label && <th>{groupingsConfig.grouping2Label}</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.forwardRows.map((row, idx) => (
                    <tr key={row.currencyPair} className={row.currencyPair === OTHER_KEY ? 'fxgo-other-row' : ''}>
                      <td className="fxgo-pair-cell">{pairLabel(row.currencyPair)}</td>
                      <td>{renderSelect(row.grouping1, ['', ...(groupingsConfig?.grouping1Values.filter(v => v.isActive).map(v => v.name) || [])], (v) => updateForwardCell(idx, 'grouping1', v))}</td>
                      {groupingsConfig?.grouping2Label && <td>{renderSelect(row.grouping2, ['', ...(groupingsConfig?.grouping2Values.filter(v => v.isActive).map(v => v.name) || [])], (v) => updateForwardCell(idx, 'grouping2', v))}</td>}
                      <td>
                        {row.currencyPair !== OTHER_KEY && (
                          <button className="fxgo-remove-btn" onClick={() => removeForwardRow(idx)} title={t('admin.fxgo.actions.remove')}>×</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {availableForwardPairs.length > 0 && (
              <div className="fxgo-add-row">
                <select value={newForwardPair} onChange={(e) => setNewForwardPair(e.target.value)}>
                  <option value="">{t('admin.fxgo.forward.selectPair')}</option>
                  {availableForwardPairs.map((cp) => <option key={cp} value={cp}>{cp}</option>)}
                </select>
                <button onClick={addForwardRow} disabled={!newForwardPair}>{t('admin.fxgo.actions.add')}</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save / Reset actions */}
      {hasChanges && (
        <div className="fxgo-add-row" style={{ marginTop: '1rem', justifyContent: 'flex-end' }}>
          <button onClick={handleReset} disabled={saving} style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
            {t('admin.fxgo.actions.reset', 'Restablecer')}
          </button>
          <button onClick={handleSave} disabled={saving}>
            {saving ? t('admin.fxgo.actions.saving', 'Guardando...') : t('admin.fxgo.actions.save', 'Guardar FXGO')}
          </button>
        </div>
      )}
    </div>
  );
};

export default FXGOSettings;
